import React, { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { useField } from 'formik'
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormFeedback,
  FormGroup,
  FormText,
  Label
} from 'reactstrap'

/**
 * Replacement `ProductCategorySelectField` that does not use react-select
 * - must be used within a Formik Form component
 * - handles single option selection
 *
 * @param {string} label - display label for the input
 * @param {booelan} required - user must fill up this filed
 * @param {func} getOptionValue - handles getting the value from the option object. Default to setting the entire object to formik value
 * @param {func} getOptionLabel - handles getting display label for the option
 * @param {Array} options - array of objects that represent the options the user can select from the dropdown menu
 * @param {boolean} isLoading - to show if the options are being fetched from API
 * @param {object} props - {`id`, `name`, `disabled` }
 */
const ProductCategorySelectDropdownField = ({
  label,
  required = false,
  formtext,
  options,
  getOptionValue = (option) => option?.id,
  getOptionLabel = (option) => option?.label,
  getOptionHeaderLabel = (option) => option?.url,
  ...props
}) => {

  const [ field, meta, helper ] = useField(props)
  const selectedOption          = field.value

  const valid           = meta.touched && !meta.error
  const invalid         = meta.touched && !!meta.error
  const validationStyle = valid
    ? 'is-valid'
    : (invalid ? 'is-invalid' : '')

  const [ dropdownOpen, setDropdownOpen ] = useState(false)

  const toggle = () => {
    if (props.disabled) return
    setDropdownOpen((prevState) => !prevState)
  }

  // Compare option.value to determine if we add or remove the selected option from Formik state
  const handleClickOption = (option) => {
    helper.setValue(option)
  }

  return (
    <FormGroup>
      { /* Conditionally render label */ }
      {
        label && (
          <Label
            for={ props.id || props.name }
            className='text-capitalize form-control-label font-weight-bold'
          >
            { label }
          </Label>
        )
      }
      {
        required && <span className='font-weight-bold text-secondary'>{ ' ' }*</span>
      }
      <Dropdown
        isOpen={ dropdownOpen }
        toggle={ toggle }
        className={ 'd-block ' }
      >
        <DropdownToggle
          tag={ 'div' }
          className={ `form-control d-flex align-items-center ${validationStyle}` }
        >

          {
            selectedOption
              ? <span className='text-capitalize'>{ getOptionLabel(selectedOption) }</span>
              : <span className='text-muted'>{ props.placeholder || 'Pilih' }</span>
          }

          <i className={ 'ml-auto fas fa-chevron-down text-muted' }/>
        </DropdownToggle>

        <DropdownMenu className='w-100'>
          {
            options.map((option, index) => {
              // will contain `options` key
              const subCategories = option.options
              const isHeader      = !!subCategories
              const header        = getOptionHeaderLabel(option)
              return (
                <Fragment key={ `product-category-dropdown-item-fragment-${index}` }>
                  {
                    isHeader && <DropdownItem header>{ header }</DropdownItem>
                  }
                  {
                    subCategories.map((subCategory) => {
                      const isSelected = getOptionValue(subCategory) === getOptionValue(selectedOption)
                      return (
                        <DropdownItem
                          key={ `category-select-${getOptionValue(subCategory)}` }
                          className='text-wrap'
                          onClick={ () => handleClickOption(subCategory) }
                          active={ isSelected }
                        >
                          { getOptionLabel(subCategory) }
                        </DropdownItem>
                      )
                    })
                  }
                </Fragment>
              )
            })
          }
        </DropdownMenu>
      </Dropdown>
      {
        formtext && <FormText>{ formtext }</FormText>
      }
      {
        invalid && <FormFeedback className={ 'd-block' }>{ meta.error }</FormFeedback>
      }
    </FormGroup>
  )
}

ProductCategorySelectDropdownField.propTypes = {
  id                   : PropTypes.string,
  value                : PropTypes.any,
  name                 : PropTypes.string,
  option               : PropTypes.object,
  type                 : PropTypes.string,
  label                : PropTypes.string.isRequired,
  required             : PropTypes.bool,
  placeholder          : PropTypes.string,
  formtext             : PropTypes.string,
  options              : PropTypes.array,
  getOptionValue       : PropTypes.func,
  getOptionLabel       : PropTypes.func,
  getOptionHeaderLabel : PropTypes.func,
  dataList             : PropTypes.arrayOf(PropTypes.object),
  disabled             : PropTypes.bool
}

export default ProductCategorySelectDropdownField